import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Merge } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  useOfficeMutations,
  useOfficeUsage,
  useOffices,
  useRegisterCounts,
} from "@/features/power_projects/hooks";
import { OFFICE_KIND_OPTIONS, type Office } from "@/features/power_projects/types";
import { useAuthStore } from "@/store/auth";
import { dupKey, duplicateKeys, officeMatches } from "../officeRegistry";
import {
  DLC02_HUB_LINKS,
  HubNav,
  PageHeader,
  TableShell,
  Th,
} from "@/features/power_projects/components/ui";

type OfficeForm = {
  name: string;
  kind: string;
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  department: string;
  contact_person: string;
  phone: string;
  email: string;
  // Adres do doręczeń elektronicznych — puste znaczy, że ten urząd trzeba obsłużyć papierowo
  // (formularz kolejki e-Doręczeń podpowiada tylko urzędy, które go mają).
  edelivery_address: string;
  bank_account: string;
  notes: string;
  is_active: boolean;
};

const EMPTY_FORM: OfficeForm = {
  name: "",
  kind: "inny",
  street: "",
  building_no: "",
  postal_code: "",
  city: "",
  department: "",
  contact_person: "",
  phone: "",
  email: "",
  edelivery_address: "",
  bank_account: "",
  notes: "",
  is_active: true,
};

const POSTAL_RE = /^\d{2}-\d{3}$/;

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="section-label">
        {label}
        {required && <span className="text-[var(--color-primary)]"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/* Rejestr jest wspólny: ten sam wpis jest adresatem wniosku, właścicielem działki i
   odbiorcą opłaty skarbowej. Zanim ktoś poprawi nazwę albo numer konta, musi widzieć,
   ilu miejsc zmiana dotknie — i że dotknie ich WSZYSTKICH, nie tylko tego, z którego tu
   wszedł. To samo ostrzeżenie odpowiada na pokusę „założę drugi urząd, będzie bezpieczniej". */
function SharedRecordNotice({ officeId }: { officeId: string }) {
  const { data: usage, isLoading } = useOfficeUsage(officeId);
  const places = usage?.by_relation ?? [];
  return (
    <div className="flex items-start gap-2 border border-amber-500/50 bg-amber-500/5 px-2.5 py-2 text-[11px]">
      <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
      <div className="space-y-0.5 text-[var(--color-text-secondary)]">
        <p>
          To <b>wspólny wpis rejestru</b> — poprawka zadziała wszędzie, gdzie ten urząd
          występuje: na działkach, we wnioskach urzędowych i w prośbach o opłaty skarbowe.
        </p>
        {isLoading ? (
          <p className="text-[var(--color-text-muted)]">Sprawdzam, gdzie jest używany…</p>
        ) : usage && usage.total > 0 ? (
          <p className="text-[var(--color-text-muted)]">
            {/* Rozbicie zamiast jednej sumy: w powiązaniach siedzi też historia zdarzeń,
                więc łączna liczba brzmiałaby groźniej, niż wynika z rzeczywistego zasięgu. */}
            Wskazany w: {places.map((p) => `${p.label}: ${p.count}`).join(" · ")}. Adres
            poprawiony ręcznie na konkretnej działce zostanie nietknięty.
          </p>
        ) : (
          <p className="text-[var(--color-text-muted)]">Nie jest jeszcze nigdzie używany.</p>
        )}
      </div>
    </div>
  );
}

export function OfficeFormModal({
  office,
  onClose,
  onCreated,
}: {
  office: Office | null;
  onClose: () => void;
  onCreated?: (office: Office) => void;
}) {
  const m = useOfficeMutations();
  const [saved, setSaved] = useState<string[] | null>(null);
  const [form, setForm] = useState<OfficeForm>(() =>
    office
      ? {
          name: office.name,
          kind: office.kind,
          street: office.street,
          building_no: office.building_no,
          postal_code: office.postal_code,
          city: office.city,
          department: office.department ?? "",
          contact_person: office.contact_person,
          phone: office.phone,
          email: office.email,
          edelivery_address: office.edelivery_address ?? "",
          bank_account: office.bank_account ?? "",
          notes: office.notes,
          is_active: office.is_active,
        }
      : EMPTY_FORM,
  );

  const set = <K extends keyof OfficeForm>(k: K, v: OfficeForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const postalOk = POSTAL_RE.test(form.postal_code.trim());
  const valid =
    form.name.trim() &&
    form.street.trim() &&
    form.building_no.trim() &&
    form.city.trim() &&
    postalOk;

  const mutation = office ? m.update : m.create;

  const submit = () => {
    if (!valid) return;
    const payload = { ...form, postal_code: form.postal_code.trim() };
    if (office) {
      m.update.mutate(
        { id: office.id, payload },
        {
          // Zamiast zamknąć w ciszy: jeśli poprawka poszła dalej (migawki na
          // właścicielach działek), pokaż dokąd — to jedyny moment, w którym widać,
          // że wspólny rekord naprawdę zadziałał wszędzie.
          onSuccess: (r) => (r.warnings.length ? setSaved(r.warnings) : onClose()),
        },
      );
    } else {
      m.create.mutate(payload, { onSuccess: (created) => { onCreated?.(created); onClose(); } });
    }
  };

  return (
    <Modal open onClose={onClose} title={office ? "Edytuj urząd" : "Nowy urząd / instytucja"}>
      <div className="space-y-3">
        {office && <SharedRecordNotice officeId={office.id} />}

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nazwa" required>
            <input className="input-base" value={form.name} autoFocus onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Rodzaj">
            <select className="input-base" value={form.kind} onChange={(e) => set("kind", e.target.value)}>
              {OFFICE_KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="border-t border-[var(--color-border-muted)] pt-3">
          <p className="section-label mb-2">Adres (dane do wysyłki papierowej)</p>
          <div className="grid sm:grid-cols-6 gap-3">
            <div className="sm:col-span-4">
              <Field label="Ulica" required>
                <input className="input-base" value={form.street} onChange={(e) => set("street", e.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Nr bud. / lok." required>
                <input className="input-base" value={form.building_no} onChange={(e) => set("building_no", e.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Kod pocztowy" required>
                <input
                  className="input-base"
                  placeholder="00-000"
                  value={form.postal_code}
                  onChange={(e) => set("postal_code", e.target.value)}
                />
                {form.postal_code.trim() && !postalOk && (
                  <p className="text-[10px] font-mono text-[var(--color-primary)] mt-0.5">Format: 00-000</p>
                )}
              </Field>
            </div>
            <div className="sm:col-span-4">
              <Field label="Miejscowość" required>
                <input className="input-base" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--color-border-muted)] pt-3 grid sm:grid-cols-2 gap-3">
          <Field label="Wydział / komórka (np. geodezji — adresat wniosków EGiB)">
            <input className="input-base" value={form.department} onChange={(e) => set("department", e.target.value)} />
          </Field>
          <Field label="Osoba kontaktowa">
            <input className="input-base" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
          </Field>
          <Field label="Telefon">
            <input className="input-base" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
          <Field label="E-mail">
            <input type="email" className="input-base" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Adres do e-Doręczeń (ADE)">
            <input
              className="input-base font-mono"
              placeholder="AE:PL-…"
              value={form.edelivery_address}
              onChange={(e) => set("edelivery_address", e.target.value)}
            />
          </Field>
          <Field label="Numer konta bankowego (opłaty urzędowe)">
            <input
              className="input-base font-mono"
              placeholder="26 cyfr (PL…)"
              value={form.bank_account}
              onChange={(e) => set("bank_account", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Uwagi">
          <textarea className="input-base min-h-[64px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
        <label className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
          />
          <span>
            Aktywny
            <span className="block text-[11px] text-[var(--color-text-muted)]">
              Odznacz, żeby wycofać urząd z list wyboru, nie ruszając spraw, które już go
              wskazują — to bezpieczniejsze niż usunięcie wpisu.
            </span>
          </span>
        </label>

        {mutation.isError && (
          <p className="text-xs text-[var(--color-primary)] font-mono">Nie udało się zapisać urzędu.</p>
        )}

        {saved ? (
          <div className="flex items-start gap-2 border border-emerald-500/50 bg-emerald-500/5 px-2.5 py-2 text-[11px] text-[var(--color-text-secondary)]">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
            <div>
              <p className="font-medium text-[var(--color-text-primary)]">Zapisano.</p>
              {saved.map((w) => <p key={w}>{w}</p>)}
            </div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {saved ? "Zamknij" : "Anuluj"}
          </Button>
          {!saved && (
            <Button size="sm" disabled={!valid} loading={mutation.isPending} onClick={submit}>
              {office ? "Zapisz" : "Utwórz"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* Scalanie duplikatów — sprzątanie po czasach, gdy rejestru nie dało się poprawić.
   Wskazane wpisy oddają rekordowi docelowemu KAŻDE powiązanie (działki, sprawy, opłaty),
   uzupełniają jego puste pola i zostają zdezaktywowane; nic nie jest usuwane. „Pokaż, co
   się stanie" to ten sam przebieg na sucho — bez niego nikt nie kliknie tego przycisku. */
export function OfficeMergeModal({
  keep,
  offices,
  onClose,
}: {
  keep: Office;
  offices: Office[];
  onClose: () => void;
}) {
  const m = useOfficeMutations();
  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [log, setLog] = useState<string[] | null>(null);
  const [done, setDone] = useState(false);

  const keepKey = dupKey(keep);
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return offices
      .filter((o) => o.id !== keep.id)
      // Duplikat zostaje zdezaktywowany — przechodzi tę samą bramkę co edycja (can_edit).
      .filter((o) => o.can_edit !== false)
      .filter((o) => !q || officeMatches(o, q))
      .sort((a, b) => {
        // Wpisy o tej samej nazwie na górę — to one są duplikatami tego rekordu.
        const same = Number(dupKey(b) === keepKey) - Number(dupKey(a) === keepKey);
        return same || a.name.localeCompare(b.name, "pl");
      })
      .slice(0, 60);
  }, [offices, search, keep.id, keepKey]);

  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const run = (dryRun: boolean) =>
    m.merge.mutate(
      { id: keep.id, duplicates: picked, dryRun },
      {
        onSuccess: (r) => {
          setLog(r.log);
          if (!r.dry_run) setDone(true);
        },
      },
    );

  return (
    <Modal open onClose={onClose} title="Scal duplikaty w ten wpis" size="lg">
      <div className="space-y-3 text-xs">
        <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2.5 py-2">
          <span className="section-label">Rekord, który zostaje</span>
          <p className="font-medium text-[var(--color-text-primary)]">{keep.name}</p>
          <p className="text-[var(--color-text-secondary)]">
            {keep.kind_display}
            {keep.department ? ` · ${keep.department}` : ""} · {keep.full_address || "bez adresu"}
          </p>
        </div>

        <p className="text-[var(--color-text-secondary)]">
          Zaznaczone wpisy oddadzą temu rekordowi wszystkie powiązania (działki, sprawy,
          opłaty), uzupełnią jego puste pola i zostaną <b>zdezaktywowane</b> — żaden nie
          zostanie usunięty.
        </p>

        {!done && (
          <>
            <input
              placeholder="Szukaj duplikatu po nazwie / mieście…"
              className="input-base"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-64 overflow-y-auto border border-[var(--color-border)] divide-y divide-[var(--color-border-muted)]">
              {candidates.map((o) => (
                <label
                  key={o.id}
                  className="flex items-start gap-2 px-2 py-1.5 cursor-pointer hover:bg-[var(--color-bg-elevated)]"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={picked.includes(o.id)}
                    onChange={() => toggle(o.id)}
                  />
                  <span className="min-w-0">
                    <span className="text-[var(--color-text-primary)]">{o.name}</span>
                    {dupKey(o) === keepKey && (
                      <span className="ml-1.5 text-[10px] font-mono uppercase text-amber-600">
                        ta sama nazwa
                      </span>
                    )}
                    {!o.is_active && (
                      <span className="ml-1.5 text-[10px] font-mono uppercase text-[var(--color-text-muted)]">
                        nieaktywny
                      </span>
                    )}
                    <span className="block text-[var(--color-text-muted)]">
                      {o.department ? `${o.department} · ` : ""}
                      {o.full_address || "bez adresu"}
                    </span>
                  </span>
                </label>
              ))}
              {candidates.length === 0 && (
                <p className="px-2 py-6 text-center section-label tracking-wide">
                  Brak innych urzędów.
                </p>
              )}
            </div>
          </>
        )}

        {log && (
          <div className="border border-dashed border-[var(--color-border-strong)] p-2 max-h-48 overflow-y-auto">
            <span className="section-label">
              {done ? "Scalono" : "Tak to zadziała (nic jeszcze nie zapisano)"}
            </span>
            <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px] text-[var(--color-text-secondary)]">
              {log.join("\n") || "Duplikaty nie mają żadnych powiązań do przepięcia."}
            </pre>
          </div>
        )}

        {m.merge.isError && (
          <p className="text-[var(--color-primary)] font-mono">Nie udało się scalić wpisów.</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {done ? "Zamknij" : "Anuluj"}
          </Button>
          {!done && (
            <>
              <Button
                size="sm"
                variant="secondary"
                disabled={!picked.length}
                loading={m.merge.isPending}
                onClick={() => run(true)}
              >
                Pokaż, co się stanie
              </Button>
              <Button
                size="sm"
                disabled={!picked.length}
                loading={m.merge.isPending}
                onClick={() => run(false)}
              >
                Scal {picked.length || ""}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function OfficesPage() {
  const { data, isLoading } = useOffices();
  // Plakietki dziennika są z DLC_02, a rejestr urzędów jest wspólny — bez modułu pytanie
  // o nie wróciłoby 403.
  const hasDLC02 = !!useAuthStore((s) => s.user)?.accessible_dlc_modules?.includes("dlc_02");
  const { data: counts } = useRegisterCounts(hasDLC02);
  const m = useOfficeMutations();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");
  const [editing, setEditing] = useState<Office | null>(null);
  const [merging, setMerging] = useState<Office | null>(null);
  const [creating, setCreating] = useState(false);

  // Deep-link from the agreement form: /paper-send/offices?new=1 opens the add form.
  // ?edit=<uuid> otwiera od razu poprawkę wskazanego urzędu — tak wchodzi się tu
  // z karty właściciela działki, gdzie błąd jest zauważany.
  useEffect(() => {
    if (searchParams.get("new")) {
      setCreating(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const editId = searchParams.get("edit");
  useEffect(() => {
    if (!editId || !data) return;
    const target = data.find((o) => o.id === editId);
    if (target && target.can_edit !== false) setEditing(target);
    searchParams.delete("edit");
    setSearchParams(searchParams, { replace: true });
  }, [editId, data, searchParams, setSearchParams]);

  // Wpisy o tej samej nazwie — ślad po czasach bez edycji. Oznaczone, żeby dało się je
  // scalić zamiast dalej mnożyć.
  const dupes = useMemo(() => duplicateKeys(data ?? []), [data]);
  // Edycja i scalanie — tylko CEO i dyrektor techniczny (backend: office_editable_by);
  // can_edit nie zależy od wpisu, więc jeden wiersz mówi za cały rejestr.
  const canManage = (data ?? []).some((o) => o.can_edit);

  const rows = useMemo(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((o) => {
      if (kind && o.kind !== kind) return false;
      if (!q) return true;
      return officeMatches(o, q);
    });
  }, [data, search, kind]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Urzędy / instytucje"
        subtitle="Wspólny rejestr — dodaj raz, poprawiaj w miejscu, wybieraj przy działkach, wnioskach i wysyłce"
        actions={<Button size="sm" onClick={() => setCreating(true)}>+ Nowy urząd</Button>}
      />

      <HubNav links={DLC02_HUB_LINKS} badges={counts?.to_acknowledge ? { "/paper-send/mine": counts.to_acknowledge } : undefined} />

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          placeholder="Szukaj po nazwie / kontakcie / mieście…"
          className="input-base sm:max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-base sm:max-w-[14rem]" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">— wszystkie rodzaje —</option>
          {OFFICE_KIND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {dupes.size > 0 && (
        <p className="mb-3 flex items-start gap-2 border border-amber-500/50 bg-amber-500/5 px-2.5 py-2 text-[11px] text-[var(--color-text-secondary)]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600" />
          <span>
            {dupes.size}{" "}
            {dupes.size === 1 ? "nazwa występuje" : "nazw występuje"} w rejestrze
            więcej niż raz.{" "}
            {canManage
              ? <>Wybierz wpis, który ma zostać, i użyj „Scal" — powiązania przejdą na
                niego, a duplikaty zostaną wyłączone z list wyboru.</>
              : <>Scalić je może dyrektor techniczny — zgłoś mu, który wpis jest właściwy.</>}
          </span>
        </p>
      )}

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Nazwa</Th>
              <Th>Rodzaj</Th>
              <Th>Adres</Th>
              <Th>Kontakt</Th>
              <Th>Status</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr
                key={o.id}
                className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors"
              >
                <td className="px-3 py-2.5">
                  <div className="font-medium text-[var(--color-text-primary)]">
                    {o.name}
                    {dupes.has(dupKey(o)) && (
                      <span
                        className="ml-1.5 text-[10px] font-mono uppercase text-amber-600"
                        title="Ta sama nazwa występuje w rejestrze więcej niż raz — scal wpisy"
                      >
                        duplikat
                      </span>
                    )}
                  </div>
                  {o.department && (
                    <div className="text-[var(--color-text-muted)]">{o.department}</div>
                  )}
                  {o.contact_person && (
                    <div className="text-[var(--color-text-muted)]">{o.contact_person}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">{o.kind_display}</td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">{o.full_address || "—"}</td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">
                  <div className="font-mono">{o.phone || "—"}</div>
                  {o.email && <div className="text-[var(--color-text-muted)]">{o.email}</div>}
                </td>
                <td className="px-3 py-2.5">
                  {o.is_active ? (
                    <span className="text-emerald-600 font-mono">aktywny</span>
                  ) : (
                    <span className="text-[var(--color-text-muted)] font-mono">nieaktywny</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {/* Edytuje, scala i usuwa tylko CEO / dyrektor techniczny (backend: office_editable_by). */}
                  {o.can_edit !== false && (
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(o)}>Edytuj</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      title="Wchłoń zdublowane wpisy tego urzędu — powiązania przejdą tutaj"
                      onClick={() => setMerging(o)}
                    >
                      <Merge size={12} /> Scal
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        // Usunięcie zrywa powiązania (SET_NULL): sprawy i działki zostają
                        // z samą kopią adresu, bez wpisu rejestru. Dezaktywacja robi to,
                        // o co zwykle chodzi — dlatego pada tu wprost.
                        if (
                          window.confirm(
                            `Usunąć urząd „${o.name}"?\n\nUsunięcie zrywa powiązanie ze sprawami i działkami, ` +
                              `które go wskazują. Jeśli chodzi Ci o wycofanie go z list wyboru, ` +
                              `lepiej odznacz „Aktywny" w edycji, a duplikat scal z właściwym wpisem.`,
                          )
                        )
                          m.remove.mutate(o.id);
                      }}
                    >
                      Usuń
                    </Button>
                  </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center section-label tracking-wide">
                  Brak urzędów.
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {(creating || editing) && (
        <OfficeFormModal
          office={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {merging && (
        <OfficeMergeModal
          keep={merging}
          offices={data ?? []}
          onClose={() => setMerging(null)}
        />
      )}
    </div>
  );
}
